export const DOCUMENT_CATEGORIES = [
  'Certified ID Copy - not older than 3 months',
  'Certified Affidavit confirming Unemployment – not Older than 3 months',
  'Certified Matric Certificate - not older than 3 months',
  'Certified Highest Qualification - not older than 3 months',
  'Media Release Form - available for online submission or hard copy',
  'Proof of Criminal Checks - we will reimburse with proof of payment and application',
  'Completed EEA1 Form - available for online submission or hard copy',
  'Official Disability Medical Certificate - If Applicable',
  'Training Agreement - online or hard copy available',
  'Internship/Learnership Agreement',
  'Workplace Agreement',
  'Proof of Stipend payment',
  'Other Documents'
] as const;

// Limited document categories for learner uploads (Phase 1)
// Only these three documents are required for new students initially
export const LEARNER_DOCUMENT_CATEGORIES = [
  'Certified ID Copy - not older than 3 months',
  'Certified Matric Certificate - not older than 3 months',
  'Certified Highest Qualification - not older than 3 months',
  'Completed EEA1 Form - available for online submission or hard copy'
] as const;

export type LearnerDocumentCategory = typeof LEARNER_DOCUMENT_CATEGORIES[number];

export type DocumentCategory = typeof DOCUMENT_CATEGORIES[number];

export const RECENCY_REQUIRED = [
  'Certified ID Copy - not older than 3 months',
  'Certified Affidavit confirming Unemployment – not Older than 3 months',
  'Certified Matric Certificate - not older than 3 months',
  'Certified Highest Qualification - not older than 3 months'
] as const satisfies readonly DocumentCategory[];

export const CATEGORY_HELPERS: Partial<Record<DocumentCategory, string>> = {
  'Certified ID Copy - not older than 3 months': 'Document must be certified and not older than 3 months.',
  'Certified Affidavit confirming Unemployment – not Older than 3 months': 'Document must be certified and not older than 3 months.',
  'Certified Matric Certificate - not older than 3 months': 'Document must be certified and not older than 3 months.',
  'Certified Highest Qualification - not older than 3 months': 'Document must be certified and not older than 3 months.',
  'Media Release Form - available for online submission or hard copy':
    'You may submit online or upload a signed hard copy.',
  'Completed EEA1 Form - available for online submission or hard copy':
    'You may submit online or upload a signed hard copy.',
  'Proof of Criminal Checks - we will reimburse with proof of payment and application':
    'Temporary acceptance of proof of payment and application. Final certificate required when available.'
};

export const CRIMINAL_CHECK_FILE_TYPES = [
  'Police Clearance Certificate',
  'Proof of Payment and Application'
] as const;

export const RECENCY_LIMIT_DAYS = 92;

export const toCategoryId = (category: DocumentCategory): string =>
  category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export interface DocumentCategoryConfig {
  id: string;
  name: DocumentCategory;
  mandatory: boolean;
  conditionalMandatory?: {
    anyOf: Array<{ field: string; values: unknown[] }>;
  };
  recencyRequired: boolean;
  helperText?: string;
  allowedFileVariants?: readonly string[];
}

const BOOLEAN_TRUE_VALUES = [true, 'true', 'yes', 'y', 1, '1'];

const conditional = (predicates: Array<{ field: string; values: unknown[] }>) => ({
  anyOf: predicates
});

export const DOCUMENT_CATEGORY_CONFIG: Record<DocumentCategory, DocumentCategoryConfig> =
  DOCUMENT_CATEGORIES.reduce<Record<DocumentCategory, DocumentCategoryConfig>>((acc, name) => {
    const baseConfig: DocumentCategoryConfig = {
      id: toCategoryId(name),
      name,
      mandatory: true,
      recencyRequired: (RECENCY_REQUIRED as readonly string[]).includes(name),
      helperText: CATEGORY_HELPERS[name]
    };

    switch (name) {
      case 'Official Disability Medical Certificate - If Applicable':
        acc[name] = {
          ...baseConfig,
          mandatory: false,
          conditionalMandatory: conditional([
            { field: 'hasDisability', values: BOOLEAN_TRUE_VALUES },
            { field: 'disabilityStatus', values: ['yes', 'pending'] },
            { field: 'disability', values: BOOLEAN_TRUE_VALUES }
          ])
        };
        break;
      case 'Proof of Stipend payment':
        acc[name] = {
          ...baseConfig,
          mandatory: false,
          conditionalMandatory: conditional([
            { field: 'receivesStipend', values: BOOLEAN_TRUE_VALUES },
            { field: 'stipendRecipient', values: BOOLEAN_TRUE_VALUES },
            { field: 'stipend', values: BOOLEAN_TRUE_VALUES },
            { field: 'receivesAllowance', values: BOOLEAN_TRUE_VALUES },
            { field: 'monthlyStipend', values: BOOLEAN_TRUE_VALUES }
          ])
        };
        break;
      case 'Proof of Criminal Checks - we will reimburse with proof of payment and application':
        acc[name] = {
          ...baseConfig,
          allowedFileVariants: CRIMINAL_CHECK_FILE_TYPES
        };
        break;
      case 'Other Documents':
        acc[name] = {
          ...baseConfig,
          mandatory: false
        };
        break;
      default:
        acc[name] = baseConfig;
    }
    return acc;
  }, {} as Record<DocumentCategory, DocumentCategoryConfig>);

export const DOCUMENT_CATEGORY_CONFIG_BY_ID = Object.values(DOCUMENT_CATEGORY_CONFIG).reduce<
  Record<string, DocumentCategoryConfig>
>((acc, config) => {
  acc[config.id] = config;
  return acc;
}, {} as Record<string, DocumentCategoryConfig>);

export const requiresRecency = (category: DocumentCategory | string): boolean => {
  const key = typeof category === 'string' ? category : (category as DocumentCategory);
  return (RECENCY_REQUIRED as readonly string[]).includes(key);
};
